import React, { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { request } from "../utils/api";
import { ViewIcon, ViewOffIcon } from "../Icons";

// Home Health Agency HR Portal styles
const hhaStyles = `
  .login-panel {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  }
`;

const ResetPassword = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get("token");
    const email = searchParams.get("email");

    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!token) {
            toast.error("Invalid or missing reset token.");
            // Optionally redirect after a delay
            // navigate('/login');
        }
    }, [token, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (password !== confirmPassword) {
            toast.error("Passwords do not match.");
            return;
        }

        if (password.length < 6) {
            toast.error("Password must be at least 6 characters.");
            return;
        }

        setIsSubmitting(true);

        try {
            // API call to reset password
            const response = await request('/reset-password', {
                method: 'POST',
                body: JSON.stringify({
                    email: String(email || "").trim().toLowerCase(),
                    token,
                    newPassword: password
                }),
                skipAuth: true,
            });
            if(response.status === 1){
                toast.success(response.message);
            } else {
                toast.error(response.message);
            }
            navigate('/login');
        } catch (error) {
            console.error("Reset password error:", error);
            toast.error(error.message || "Failed to reset password. Token may be expired.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <style>{hhaStyles}</style>
            <div className="min-h-screen flex bg-white font-sans text-gray-900 overflow-hidden">
                {/* Left Side - kornivusinfotech.png (hidden on mobile & tablet) */}
                <div className="hidden lg:flex lg:w-1/2 relative min-h-screen overflow-hidden bg-gradient-to-br from-indigo-50 via-sky-50/90 to-indigo-100">
                    <img
                        src="/kornivusinfotech.png"
                        alt="Kornivus Infotech"
                        className="absolute inset-0 w-full h-full object-cover object-center"
                        onError={(e) => {
                            e.target.style.display = "none";
                        }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-indigo-900/5 via-transparent to-transparent pointer-events-none" aria-hidden />
                </div>

                {/* Right Side - Reset Password Form */}
                <div className="w-full lg:w-1/2 flex flex-col justify-center items-center p-6 sm:p-12 xl:p-24 bg-white relative">
                    <div className="absolute inset-0 lg:hidden pointer-events-none opacity-[0.02]"
                        style={{ backgroundImage: "radial-gradient(#000 1px, transparent 1px)", backgroundSize: "30px 30px" }}
                    />
                    <div className="relative z-10 w-full max-w-lg flex flex-col items-center">
                        {/* White Rounded Panel */}
                        <div className="w-full bg-white rounded-[2rem] p-10 shadow-[0_20px_50px_rgba(0,0,0,0.05)] border border-gray-100">
                        {/* Logo Section */}
                        <div className="mb-8 flex items-center justify-center">
                            <img
                                src="/login-logo.png"
                                alt="Logo"
                                className="h-16 w-auto object-contain"
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                }}
                            />
                            <img
                                src="/logo.png.png"
                                alt="Logo"
                                className="h-16 w-auto object-contain"
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                }}
                            />
                        </div>

                        {/* Title Section */}
                        <div className="mb-10 text-center">
                            <h2 className="text-2xl font-bold text-gray-900 tracking-tight">
                                Reset Password
                            </h2>
                            <p className="text-gray-500 mt-2">
                                Enter your new password below
                            </p>
                        </div>

                        {!token ? (
                            <div className="text-center">
                                <div className="bg-red-50 border border-red-100 text-red-600 px-4 py-4 rounded-xl mb-6">
                                    <p className="font-semibold">Invalid Link</p>
                                    <p className="text-sm mt-1">This password reset link is invalid or has expired.</p>
                                </div>
                                <Link to="/forgot-password" className="text-blue-600 hover:text-blue-800 font-semibold">
                                    Request a new link
                                </Link>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-6">
                                {/* New Password Input */}
                                <div className="space-y-2">
                                    <label htmlFor="password" className="block text-sm font-semibold text-gray-700">
                                        New Password
                                    </label>
                                    <div className="relative">
                                        <input
                                            id="password"
                                            type={showPassword ? "text" : "password"}
                                            placeholder="••••••••"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className="block w-full px-4 py-3.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-gray-900 bg-gray-50/50 hover:bg-white transition-all pr-12"
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                                        >
                                            {showPassword ? <ViewOffIcon className="w-5 h-5" /> : <ViewIcon className="w-5 h-5" />}
                                        </button>
                                    </div>
                                </div>

                                {/* Confirm Password Input */}
                                <div className="space-y-2">
                                    <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-700">
                                        Confirm Password
                                    </label>
                                    <div className="relative">
                                        <input
                                            id="confirmPassword"
                                            type={showConfirmPassword ? "text" : "password"}
                                            placeholder="••••••••"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            className="block w-full px-4 py-3.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-gray-900 bg-gray-50/50 hover:bg-white transition-all pr-12"
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                                        >
                                            {showConfirmPassword ? <ViewOffIcon className="w-5 h-5" /> : <ViewIcon className="w-5 h-5" />}
                                        </button>
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 px-6 rounded-xl font-bold text-base shadow-lg shadow-blue-200 hover:shadow-blue-300 transform hover:scale-[1.01] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-3 mt-4"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            <span>Resetting...</span>
                                        </>
                                    ) : (
                                        <span>Reset Password</span>
                                    )}
                                </button>

                                <div className="text-center mt-4">
                                    <Link to="/login" className="text-sm text-blue-600 hover:text-blue-700 font-semibold">
                                        Back to Login
                                    </Link>
                                </div>
                            </form>
                        )}
                    </div>

                    {/* Footer */}
                    <footer className="mt-12 text-center text-gray-400 text-sm">
                        <p>Copyright &copy; {new Date().getFullYear()} Kornivus Infotech. All rights reserved.</p>
                    </footer>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ResetPassword;
